import { WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR } from "@/constants";
import { REVIEWS_WIDGET_NAMES } from "@/constants/widget-names";
import { REVIEWS_SLIDER_IMAGES_TYPE } from "@apps/reviews/constants";
import { ReviewsSliderWidgetDefinition } from "@apps/reviews/definitions";
import AnalyticsEventsCollectorInterface from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsCollectorInterface";
import { AnalyticsEventsFactory } from "@modules/analytics_events_collector/AnalyticsEventsCollector/AnalyticsEventsFactory";
import { DynamicImportManager } from "@modules/dynamic_import/DynamicImportManager";
import WidgetInjectedSdkEvent from "@modules/sdk/common_sdk/events/WidgetInjectedSdkEvent";
import { dispatchSdkEvent } from "@modules/sdk/helpers";
import IntersectionObserverInjector from "@ui-utils/Injector/IntersectionObserverInjector";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import {
    parseBooleanDataAttr,
    parseIntDataAttr,
    parseOptionalBooleanDataAttr,
    parseStringDataAttr,
} from "@ui-utils/Injector/utils";
import { create } from "superstruct";

import { LoggerInterface } from "@interfaces/LoggerInterface";

import { ReviewsSliderWidgetConfigSchema } from "./ReviewsSliderWidgetConfig";

const LOWEST_RATING_DATA_ATTR = "gw-lowest-rating";
const IMAGES_TYPE_DATA_ATTR = "gw-images-type";
const ONLY_PINNED_REVIEWS_DATA_ATTR = "gw-only-pinned-reviews";
const REVIEWS_SEQUENCE_DATA_ATTR = "gw-reviews-sequence";

const LOWEST_RATING_DEFAULT = 4;
const IMAGES_TYPE_DEFAULT = REVIEWS_SLIDER_IMAGES_TYPE.PRODUCT;

class ReviewsSliderWidgetInjector {
    private ioi: IntersectionObserverInjector;

    constructor(
        private readonly placeholderSelector: string,
        private readonly logger: LoggerInterface,
        private readonly eventsCollector: AnalyticsEventsCollectorInterface,
        private readonly analyticsEventsFactory: AnalyticsEventsFactory,
        private readonly dynamicImportManager: DynamicImportManager,
        private readonly moi: MutationObserverInjector
    ) {
        this.ioi = new IntersectionObserverInjector(
            IntersectionObserver,
            this.moi,
            this.placeholderSelector,
            {
                rootMargin: "100px 0px 0px 0px",
            }
        );
    }

    start(): void {
        this.logger.debug("ReviewsSliderWidgetInjector start", {
            placeholderSelector: this.placeholderSelector,
        });

        this.ioi.targets$.subscribe((placeholder) => {
            this.logger.debug("ReviewsSliderWidgetInjector found placeholder"),
                {
                    placeholder,
                };

            const lowestRatingFromDataAttr =
                parseIntDataAttr(placeholder, LOWEST_RATING_DATA_ATTR) ||
                LOWEST_RATING_DEFAULT;

            const imagesTypeFromDataAttr =
                parseStringDataAttr(placeholder, IMAGES_TYPE_DATA_ATTR) ||
                IMAGES_TYPE_DEFAULT;

            const onlyPinnedReviewsFromDataAttr = parseBooleanDataAttr(
                placeholder,
                ONLY_PINNED_REVIEWS_DATA_ATTR
            );

            const reviewsSequenceFromDataAttr = parseStringDataAttr(
                placeholder,
                REVIEWS_SEQUENCE_DATA_ATTR
            );

            void (async () => {
                const ReviewsSliderWidget =
                    await this.dynamicImportManager.importDefault(
                        ReviewsSliderWidgetDefinition,
                        true
                    );

                const validConfig = create(
                    {
                        target: placeholder,
                        lowestRating: lowestRatingFromDataAttr,
                        imagesType: imagesTypeFromDataAttr,
                        onlyPinnedReviews: onlyPinnedReviewsFromDataAttr,
                        reviewsSequence:
                            reviewsSequenceFromDataAttr || "default",
                        isWidgetShadowRootEnabled: parseOptionalBooleanDataAttr(
                            placeholder,
                            WIDGET_SHADOW_ROOT_ENABLED_DATA_ATTR
                        ),
                    },
                    ReviewsSliderWidgetConfigSchema
                );

                const widget = new ReviewsSliderWidget(
                    validConfig,
                    this.logger
                );

                await widget.init();

                this.eventsCollector.pushEvent(
                    this.analyticsEventsFactory.createWidgetInjected(
                        REVIEWS_WIDGET_NAMES.REVIEWS_SLIDER_WIDGET
                    )
                );

                dispatchSdkEvent(
                    new WidgetInjectedSdkEvent(
                        REVIEWS_WIDGET_NAMES.REVIEWS_SLIDER_WIDGET
                    )
                );
            })();
        });

        this.ioi.start();
    }
}

export default ReviewsSliderWidgetInjector;
